import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Subscription, PaidPool, PoolEntry } from '@bracketblitz/shared-types'

interface PremiumState {
  // Subscription state
  isPremium: boolean
  subscription: Subscription | null
  isLoading: boolean
  error: string | null
  
  // Pools state
  pools: PaidPool[]
  myPools: PaidPool[]
  currentPool: PaidPool | null
  poolEntries: PoolEntry[]
  
  // Actions - Subscription
  subscribe: (planId: string, paymentMethodId: string) => Promise<void>
  cancelSubscription: (reason?: string) => Promise<void>
  checkSubscriptionStatus: () => Promise<void>
  
  // Actions - Pools
  fetchPools: () => Promise<void>
  fetchMyPools: () => Promise<void>
  createPool: (config: Partial<PaidPool>) => Promise<PaidPool>
  joinPool: (poolId: string, paymentMethodId: string) => Promise<void>
  leavePool: (poolId: string) => Promise<void>
  getPoolDetails: (poolId: string) => Promise<PaidPool>
  
  // Utility actions
  clearError: () => void
  reset: () => void
}

const initialState = {
  isPremium: false,
  subscription: null,
  isLoading: false,
  error: null,
  pools: [],
  myPools: [],
  currentPool: null,
  poolEntries: [],
}

// Mock API calls for development
const mockAPI = {
  subscribe: async (planId: string, _paymentMethodId: string): Promise<Subscription> => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000))
    
    return {
      id: `sub_${Date.now()}`,
      userId: 'user123',
      planId,
      status: 'active',
      plan: {
        name: 'BracketBlitz Premium',
        description: 'Unlock all premium features for the season',
        price: 4.99,
        currency: 'usd',
        interval: 'yearly',
        features: [
          'Unlimited power-ups',
          'Advanced MarchMetrix analytics',
          'Custom bracket themes',
          'Ad-free experience',
          'Early upset alerts',
          'Exclusive premium challenges',
          'Historical tournament data',
          'Priority support'
        ]
      },
      billing: {
        provider: 'stripe',
        customerId: 'cus_123',
        subscriptionId: 'sub_123',
        amount: 4.99,
        currency: 'usd'
      },
      startDate: new Date().toISOString(),
      isTrialActive: false,
      usage: {
        powerUpsPurchased: 0,
        specialEventsEntered: 0,
        premiumLeaguesJoined: 0,
        customBracketsCreated: 0,
        advancedAnalyticsViews: 0
      },
      benefits: {
        unlimitedBrackets: true,
        premiumPowerUps: true,
        advancedAnalytics: true,
        prioritySupport: true,
        exclusiveEvents: true,
        customThemes: true,
        adFree: true,
        earlyAccess: true
      }
    }
  },
  
  createPool: async (config: Partial<PaidPool>): Promise<PaidPool> => {
    await new Promise(resolve => setTimeout(resolve, 800))
    
    const pool: PaidPool = {
      id: `pool_${Date.now()}`,
      name: config.name || 'Untitled Pool',
      description: config.description || '',
      config: {
        entryFee: config.config?.entryFee || 10,
        currency: 'usd',
        maxParticipants: config.config?.maxParticipants || 100,
        minParticipants: 2,
        isPrivate: config.config?.isPrivate || false,
        inviteCode: config.config?.isPrivate ? `INVITE${Math.random().toString(36).substr(2, 6).toUpperCase()}` : undefined
      },
      rules: {
        scoringSystem: 'standard',
        allowPowerUps: true,
        bracketLockTime: '2025-03-21T12:00:00Z',
        payoutStructure: [
          { rank: 1, percentage: 60, amount: 0, description: 'First Place' },
          { rank: 2, percentage: 25, amount: 0, description: 'Second Place' },
          { rank: 3, percentage: 15, amount: 0, description: 'Third Place' }
        ]
      },
      status: 'open',
      currentParticipants: 1,
      totalPrizePool: 0,
      openDate: new Date().toISOString(),
      closeDate: '2025-03-21T11:00:00Z',
      startDate: '2025-03-21T12:00:00Z',
      endDate: '2025-04-08T23:59:59Z',
      createdBy: 'user123',
      organizerId: 'user123',
      moderators: ['user123'],
      finances: {
        totalCollected: 0,
        platformFee: 0,
        organizerFee: 0,
        netPrizePool: 0,
        payoutsPending: 0,
        payoutsCompleted: 0
      }
    }
    
    return pool
  },
  
  fetchPools: async (): Promise<PaidPool[]> => {
    await new Promise(resolve => setTimeout(resolve, 500))
    
    // Mock pool data
    return [
      {
        id: 'pool1',
        name: 'March Madness Master',
        description: 'High stakes tournament for serious bracket builders',
        config: {
          entryFee: 50,
          currency: 'usd',
          maxParticipants: 100,
          minParticipants: 10,
          isPrivate: false
        },
        rules: {
          scoringSystem: 'standard',
          allowPowerUps: true,
          bracketLockTime: '2025-03-21T12:00:00Z',
          payoutStructure: [
            { rank: 1, percentage: 50, amount: 2500, description: 'Champion' },
            { rank: 2, percentage: 25, amount: 1250, description: 'Runner-up' },
            { rank: 3, percentage: 15, amount: 750, description: 'Third Place' },
            { rank: { min: 4, max: 10 }, percentage: 10, amount: 500, description: 'Top 10' }
          ]
        },
        status: 'open',
        currentParticipants: 75,
        totalPrizePool: 4500,
        openDate: '2025-01-15T00:00:00Z',
        closeDate: '2025-03-21T11:00:00Z',
        startDate: '2025-03-21T12:00:00Z',
        endDate: '2025-04-08T23:59:59Z',
        createdBy: 'admin1',
        organizerId: 'admin1',
        moderators: ['admin1'],
        finances: {
          totalCollected: 3750,
          platformFee: 375,
          organizerFee: 0,
          netPrizePool: 3375,
          payoutsPending: 0,
          payoutsCompleted: 0
        }
      },
      {
        id: 'pool2',
        name: 'Casual Champions',
        description: 'Fun low-stakes pool for casual players',
        config: {
          entryFee: 5,
          currency: 'usd',
          maxParticipants: 1000,
          minParticipants: 10,
          isPrivate: false
        },
        rules: {
          scoringSystem: 'standard',
          allowPowerUps: false,
          bracketLockTime: '2025-03-21T12:00:00Z',
          payoutStructure: [
            { rank: 1, percentage: 70, amount: 3500, description: 'Winner Takes All' }
          ]
        },
        status: 'open',
        currentParticipants: 892,
        totalPrizePool: 4005,
        openDate: '2025-01-01T00:00:00Z',
        closeDate: '2025-03-21T11:00:00Z',
        startDate: '2025-03-21T12:00:00Z',
        endDate: '2025-04-08T23:59:59Z',
        createdBy: 'user456',
        organizerId: 'user456',
        moderators: ['user456'],
        finances: {
          totalCollected: 4460,
          platformFee: 446,
          organizerFee: 0,
          netPrizePool: 4014,
          payoutsPending: 0,
          payoutsCompleted: 0
        }
      }
    ]
  }
}

export const usePremiumStore = create<PremiumState>()(
  persist(
    (set, get) => ({
      ...initialState,

      // Subscription actions
      subscribe: async (planId: string, paymentMethodId: string) => {
        set({ isLoading: true, error: null })
        
        try {
          const subscription = await mockAPI.subscribe(planId, paymentMethodId)
          set({ 
            subscription, 
            isPremium: true, 
            isLoading: false 
          })
        } catch (error) {
          set({ 
            error: error instanceof Error ? error.message : 'Failed to subscribe',
            isLoading: false 
          })
        }
      },

      cancelSubscription: async (reason?: string) => {
        set({ isLoading: true, error: null })
        
        try {
          // Simulate API call
          await new Promise(resolve => setTimeout(resolve, 1000))
          
          const { subscription } = get()
          if (subscription) {
            const updatedSubscription = {
              ...subscription,
              status: 'cancelled' as const,
              cancelledAt: new Date().toISOString(),
              cancellationReason: reason
            }
            
            set({ 
              subscription: updatedSubscription,
              isPremium: false,
              isLoading: false 
            })
          }
        } catch (error) {
          set({ 
            error: error instanceof Error ? error.message : 'Failed to cancel subscription',
            isLoading: false 
          })
        }
      },

      checkSubscriptionStatus: async () => {
        set({ isLoading: true, error: null })
        
        try {
          // Simulate API call to check subscription status
          await new Promise(resolve => setTimeout(resolve, 500))
          
          const { subscription } = get()
          if (subscription && subscription.status === 'active') {
            set({ isPremium: true })
          }
          
          set({ isLoading: false })
        } catch (error) {
          set({ 
            error: error instanceof Error ? error.message : 'Failed to check subscription status',
            isLoading: false 
          })
        }
      },

      // Pool actions
      fetchPools: async () => {
        set({ isLoading: true, error: null })
        
        try {
          const pools = await mockAPI.fetchPools()
          set({ pools, isLoading: false })
        } catch (error) {
          set({ 
            error: error instanceof Error ? error.message : 'Failed to fetch pools',
            isLoading: false 
          })
        }
      },

      fetchMyPools: async () => {
        set({ isLoading: true, error: null })
        
        try {
          // Filter pools where user is a participant
          const { pools } = get()
          const myPools = pools.filter(pool => 
            pool.createdBy === 'user123' // In real app, use actual user ID
          )
          
          set({ myPools, isLoading: false })
        } catch (error) {
          set({ 
            error: error instanceof Error ? error.message : 'Failed to fetch your pools',
            isLoading: false 
          })
        }
      },

      createPool: async (config: Partial<PaidPool>) => {
        set({ isLoading: true, error: null })
        
        try {
          const newPool = await mockAPI.createPool(config)
          const { pools, myPools } = get()
          
          set({ 
            pools: [...pools, newPool],
            myPools: [...myPools, newPool],
            isLoading: false 
          })
          
          return newPool
        } catch (error) {
          set({ 
            error: error instanceof Error ? error.message : 'Failed to create pool',
            isLoading: false 
          })
          throw error
        }
      },

      joinPool: async (poolId: string, _paymentMethodId: string) => {
        set({ isLoading: true, error: null })
        
        try {
          // Simulate API call
          await new Promise(resolve => setTimeout(resolve, 1000))
          
          const { pools } = get()
          const updatedPools = pools.map(pool => 
            pool.id === poolId 
              ? { 
                  ...pool, 
                  currentParticipants: pool.currentParticipants + 1,
                  totalPrizePool: pool.totalPrizePool + pool.config.entryFee
                }
              : pool
          )
          
          set({ pools: updatedPools, isLoading: false })
        } catch (error) {
          set({ 
            error: error instanceof Error ? error.message : 'Failed to join pool',
            isLoading: false 
          })
        }
      },

      leavePool: async (poolId: string) => {
        set({ isLoading: true, error: null })
        
        try {
          // Simulate API call
          await new Promise(resolve => setTimeout(resolve, 800))
          
          const { pools } = get()
          const updatedPools = pools.map(pool => 
            pool.id === poolId 
              ? { 
                  ...pool, 
                  currentParticipants: Math.max(0, pool.currentParticipants - 1),
                  totalPrizePool: Math.max(0, pool.totalPrizePool - pool.config.entryFee)
                }
              : pool
          )
          
          set({ pools: updatedPools, isLoading: false })
        } catch (error) {
          set({ 
            error: error instanceof Error ? error.message : 'Failed to leave pool',
            isLoading: false 
          })
        }
      },

      getPoolDetails: async (poolId: string) => {
        const { pools } = get()
        const pool = pools.find(p => p.id === poolId)
        
        if (!pool) {
          throw new Error('Pool not found')
        }
        
        set({ currentPool: pool })
        return pool
      },

      // Utility actions
      clearError: () => set({ error: null }),
      
      reset: () => set(initialState),
    }),
    {
      name: 'premium-storage',
      partialize: (state) => ({
        isPremium: state.isPremium,
        subscription: state.subscription,
      }),
    }
  )
)